<template>
    <div class="error-msg-wrap">
        <div class="form-wrapper slide-up">
            <div class="form-close">
                <button type="button" class="form-close-x" title="Close" @click="$emit('close')">&times;</button>
            </div>
            <h3>Name</h3>
            <input type="text" v-model="formData.Name" @input="setSlug()"/>
            <div>
                <h3 v-if="formData.isNew">Slug</h3>
                <input v-if="formData.isNew" type="text" v-model="formData.Slug" @input="editSlug()"/>
                <div v-if="!$props.node">
                    <h3>Type</h3>
                    <select v-model="formData.Type">
                        <option value="PROCESS">Process</option>
                        <option value="TIMELINE">Value Engine</option>
                        <option value="SHEET">Sheet</option>
                        <option value="CHECKLIST">Checklist</option>
                        <option value="LINK">Link</option>
                        <option value="VIDEO">Video</option>
                        <option value="NODE">Folder</option>
                    </select>
                </div>
            </div>
            <div v-if="(formData.Type == 'PROCESS' || formData.Type == 'CHECKLIST' || formData.Type == 'SHEET' || formData.Type == 'TIMELINE') && formData.isNew && !cloneNode" style="margin: 5px 0 20px">
                <div class="ai-btn" @click="showAIModal = true">
                    <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M13 0L3.5 14H10l-1.5 10L20 9h-6.5L15 0h-2z"/></svg>
                    Generate with AI
                </div>
                <div v-if="formData.Type == 'PROCESS' && aiProcessJSON" style="margin-top:8px;color:var(--ai-accent-deep);font-weight:600;font-size:14px">
                    &#10003; AI draft attached &mdash; the process content will be filled in automatically
                </div>
                <div v-if="formData.Type == 'CHECKLIST' && aiChecklistJSON" style="margin-top:8px;color:var(--ai-accent-deep);font-weight:600;font-size:14px">
                    &#10003; AI draft attached &mdash; the checklist items will be filled in automatically
                </div>
                <div v-if="formData.Type == 'SHEET' && aiSheetJSON" style="margin-top:8px;color:var(--ai-accent-deep);font-weight:600;font-size:14px">
                    &#10003; AI draft attached &mdash; the sheet grid will be filled in automatically
                </div>
                <div v-if="formData.Type == 'TIMELINE' && aiTimelineJSON" style="margin-top:8px;color:var(--ai-accent-deep);font-weight:600;font-size:14px">
                    &#10003; AI draft attached &mdash; the value engine steps will be filled in automatically
                </div>
            </div>
            <!-- A new role sheet's sections: the default role sheet template (or the standard sections when there's none), or
                 another template. An AI draft brings its own sections, so the choice goes away once one is attached. -->
            <div v-if="formData.Type == 'SHEET' && formData.isNew && !cloneNode && !aiSheetJSON && (sheetTemplates.length || canManageSheetTemplates)" style="margin-bottom:20px">
                <h3>Start from</h3>
                <select v-model="sheetTemplateID" style="margin-bottom:6px">
                    <option :value="null">Default</option>
                    <option v-for="t in otherSheetTemplates" :key="t.ID" :value="t.ID">{{ t.Name }}</option>
                </select>
                <div style="font-size:12.5px;line-height:1.45;color:var(--font-mid-gray)">
                    <div v-if="chosenSheetTemplate && chosenSheetTemplate.Description">{{ chosenSheetTemplate.Description }}</div>
                    <div>Sections: {{ chosenSheetSections.map((s) => s.label).join(', ') }}</div>
                    <a v-if="canManageSheetTemplates" style="text-decoration:underline;cursor:pointer" @click.prevent="manageSheetTemplates()">Manage templates</a>
                </div>
            </div>
            <div v-if="formData.Type == 'LINK' || formData.Type == 'VIDEO'">
                <h3>Link</h3>
                <input type="text" v-model="formData.Link" @input="checkLink()"/>
            </div>
            <div v-if="formData.Type == 'LINK' || formData.Type == 'VIDEO'" style="margin-bottom:25px">
                <drop-file @fileChange="onFileChange" />
            </div>
            <div v-if="formData.Type == 'PROCESS' || formData.Type == 'SHEET' || formData.Type == 'TIMELINE'">
                <h3>Process Owner</h3>
                <process-owner-picker v-model="ownerIDs" style="margin-bottom:20px" />
            </div>
            <h3>Locations</h3>
            <div style="margin-bottom: 20px;max-height:80px;overflow:auto">
                <div v-for="(location, i) in formData.LocationsNew" :key="i" class="checkbox-input">
                    <input type="checkbox" v-model="location.isChecked" />
                    <div>{{ location.name }}</div>
                </div>
            </div>
            <h3>Departments</h3>
            <div style="margin-bottom: 20px;max-height:80px;overflow:auto">
                <div v-for="(department, i) in formData.DepartmentsNew" :key="i" class="checkbox-input">
                    <input type="checkbox" v-model="department.isChecked" />
                    <div>{{ department.name }}</div>
                </div>
            </div>
            <h3 v-if="formData.Type != 'NODE'">Types</h3>
            <div v-if="formData.Type != 'NODE'" style="margin-bottom: 20px;max-height:80px;overflow:auto">
                <div v-for="(type, i) in formData.TypesNew" :key="i" class="checkbox-input">
                    <input type="checkbox" v-model="type.isChecked" />
                    <div>{{ type.name }}</div>
                </div>
            </div>
            <!-- Same status choice as the editor's save dialog, starting on the current one, so saving details never publishes a draft -->
            <div v-if="showStatus" style="margin-bottom:20px">
                <h3>Status</h3>
                <select v-model="status">
                    <option value="Draft">Draft</option>
                    <option value="Inactive">Inactive</option>
                    <option value="Needs Approval">Needs Approval</option>
                    <option value="">Published/Approved</option>
                </select>
            </div>
            <!-- Same choice as the editor's save dialog; not on a first version (v0), there's nothing signed yet -->
            <div v-if="showSignChoice" style="margin-bottom:20px">
                <label :class="['ep-opt', { on: !minorChange }]">
                    <input v-model="minorChange" type="radio" :value="false" />
                    <span>
                        <b>Yes, people need to sign again</b>
                        <small>Saves as v{{ formData.Version + 1 }}. Everyone assigned is asked to sign it.</small>
                    </span>
                </label>
                <label :class="['ep-opt', { on: minorChange }]">
                    <input v-model="minorChange" type="radio" :value="true" />
                    <span>
                        <b>No, it's a small fix</b>
                        <small>Stays v{{ formData.Version }}, and anyone who signed it stays signed.</small>
                    </span>
                </label>
            </div>
            <input :class="disableButton ? 'disable-button' : ''" type="submit" value="Submit" @click="submit()" />
        </div>
        <ai-process-modal v-if="showAIModal" mode="generate" :node-type="formData.Type == 'CHECKLIST' ? 'checklist' : formData.Type == 'SHEET' ? 'sheet' : formData.Type == 'TIMELINE' ? 'timeline' : 'process'" @close="showAIModal = false" @accept="onAIDraft" />
    </div>
</template>

<script lang="ts">
import { defineComponent, registerRuntimeCompiler } from 'vue';
import axios from 'axios';
import DropFile from '@/components/DropFile.vue';
import AIProcessModal from '@/components/AIProcessModal.vue';
import ProcessOwnerPicker from '@/components/ProcessOwnerPicker.vue';
import { newRoleSheet, DEFAULT_SECTIONS } from '@/roleSheet';

export default defineComponent({
    name: 'AddEditNode',
    props: [ 'node', 'fromSlug', 'type', 'cloneNode' ],
    components: {
        DropFile,
        AiProcessModal: AIProcessModal,
        ProcessOwnerPicker
    },
    data() {
        return {
            disableButton: false,
            showAIModal: false,
            aiProcessJSON: null as any,
            aiVideoJobId: null as any,
            aiChecklistJSON: null as any,
            aiSheetJSON: null as any,
            aiSheetColsJSON: null as any,
            aiTimelineJSON: null as any,
            formData: {
                Name: '',
                Type: 'PROCESS',
                Link: null as any,
                Slug: '',
                ProcessOwnersJSON: '[]',
                isNew: false,
                fromSlug: null,
                ProcessJSON: null as any,
                TimelineJSON: null as any,
                SheetJSON: null as any,
                SheetColsJSON: null as any,
                ChecklistJSON: null as any,
                LocationsJSON: null as any,
                LocationsNew: [] as any,
                DepartmentsJSON: null as any,
                DepartmentsNew: [] as any,
                TypesJSON: null as any,
                TypesNew: [] as any,
                metaUpdate: true,
                Version: 1
            },
            requiredFields: [
                'Name', 'Type', 'Slug'
            ],
            defaultProcessJSON: {
                items: [
                    {
                        header: '',
                        body: '',
                        image: ''
                    }
                ]
            },
            defaultTimelineJSON: {
                items: [
                    {
                        lineOne: '',
                        lineTwo: '',
                        lineThree: '',
                        color: '#71E2FB',
                        description: ''
                    }
                ]
            },
            defaultChecklistJSON: {
                items: []
            },
            // tblUser IDs, saved as formData.ProcessOwnersJSON on submit
            ownerIDs: [] as number[],
            // Small fix: saved without a new version or LastModified, so current sign-offs stay current
            minorChange: false,
            // Edit Details status: '' is Published, else the NeedsApprovalType. Only offered when the node passed in
            // came with its status (hasStatus), so a node loaded without it is never published by mistake.
            status: '',
            hasStatus: false,
            // Role sheet templates (API rolesheettemplates.ts), with Sections parsed; sheetTemplateID: the one picked, null for Default
            sheetTemplates: [] as any[],
            sheetTemplateID: null as any
        }
    },
    computed: {
        canManageSheetTemplates(): boolean {
            return this.$store.state.user.role == 'Admin' || !!this.$store.state.user.superAdmin;
        },
        otherSheetTemplates(): any[] {
            return this.sheetTemplates.filter((t: any) => !t.IsDefault);
        },
        // The template a new sheet starts from: the one picked, else the default (none: the standard sections)
        chosenSheetTemplate(): any {
            return this.sheetTemplates.find((t: any) => this.sheetTemplateID ? t.ID == this.sheetTemplateID : t.IsDefault) || null;
        },
        chosenSheetSections(): { label: string, hint?: string }[] {
            return this.chosenSheetTemplate ? this.chosenSheetTemplate.Sections : DEFAULT_SECTIONS.map(label => ({ label }));
        },
        showSignChoice(): boolean {
            const f: any = this.formData;
            return !f.isNew && f.Version > 0 && [ 'PROCESS', 'TIMELINE', 'SHEET', 'CHECKLIST' ].includes(f.Type) && !this.unpublishedSave;
        },
        showStatus(): boolean {
            const f: any = this.formData;
            return this.hasStatus && !f.isNew && [ 'PROCESS', 'TIMELINE', 'SHEET', 'CHECKLIST' ].includes(f.Type);
        },
        // Saving as anything but Published/Approved is always a small fix (no new version to sign)
        unpublishedSave(): boolean {
            const f: any = this.formData;
            return this.showStatus && !!this.status && f.Version > 0;
        }
    },
    async created() {
        if(this.$props.node) {
            this.formData = this.$props.node;
            this.ownerIDs = (this.$props.node.ProcessOwners || []).map((o: any) => o.UserID);
            if('NeedsApproval' in this.$props.node) {
                this.hasStatus = true;
                this.status = this.$props.node.NeedsApproval ? (this.$props.node.NeedsApprovalType || 'Draft') : '';
            }
        }

        this.formData.LocationsNew = [];
        for(const location of this.$store.state.user.locations) {
            this.formData.LocationsNew.push({
                name: location,
                isChecked: false
            })
        }

        this.formData.DepartmentsNew = [];
        for(const department of this.$store.state.user.departments) {
            this.formData.DepartmentsNew.push({
                name: department,
                isChecked: false
            })
        }

        this.formData.TypesNew = [];
        for(const type of this.$store.state.user.nodeTypes) {
            this.formData.TypesNew.push({
                name: type,
                isChecked: false
            })
        }

        if(this.$props.node) {
            if(this.$props.node.LocationsJSON) {
                const locationsJSON = JSON.parse(this.$props.node.LocationsJSON);
                for(const location of locationsJSON) {
                    const formLocation = this.formData.LocationsNew.find((loc: any) => {
                        return loc.name == location;
                    })
                    if(formLocation) {
                        formLocation.isChecked = true;
                    }
                }
            }

            if(this.$props.node.DepartmentsJSON) {
                const departmentsJSON = JSON.parse(this.$props.node.DepartmentsJSON);
                for(const dep of departmentsJSON) {
                    const formDep = this.formData.DepartmentsNew.find((d: any) => {
                        return d.name == dep;
                    })
                    if(formDep) {
                        formDep.isChecked = true;
                    }
                }
            }

            if(this.$props.node.TypesJSON) {
                const typesJSON = JSON.parse(this.$props.node.TypesJSON);
                for(const type of typesJSON) {
                    const formType = this.formData.TypesNew.find((t: any) => {
                        return t.name == type;
                    })
                    if(formType) {
                        formType.isChecked = true;
                    }
                }
            }

            if(this.cloneNode) {
                this.formData.isNew = true;
            }
            
        } else {
            this.formData.isNew = true;
        }
        if(this.$props.type && this.$props.type != 'ALL') {
            this.formData.Type = this.$props.type;
        }
        this.formData.fromSlug = this.$props.fromSlug;

        // Only a new node can start from a role sheet template; its type can still change to Sheet in the form
        if(this.formData.isNew && !this.cloneNode) {
            await axios.get(`${this.$store.state.apiUrl}/api/rolesheettemplates`)
                .then(resp => {
                    this.sheetTemplates = resp.data.map((t: any) => ({ ...t, Sections: JSON.parse(t.SectionsJSON || '[]') }));
                })
                .catch(e => {
                    console.log(e);
                })
        }
    },
    methods: {
        manageSheetTemplates() {
            this.$emit('close');
            this.$router.push('/role-sheet-templates');
        },
        async submit() {
            const checkedLocations = this.formData.LocationsNew.filter((location: any) => {
                return location.isChecked;
            })
            if(!checkedLocations.length) {
                alert('Include at least one location');
                return;
            }
            const locationsJSON = [];
            for(const location of checkedLocations) {
                locationsJSON.push(location.name);
            }
            this.formData.LocationsJSON = JSON.stringify(locationsJSON);

            const checkedDepartments = this.formData.DepartmentsNew.filter((dep: any) => {
                return dep.isChecked;
            })
            if(!checkedDepartments.length) {
                alert('Include at least one department');
                return;
            }
            const departmentsJSON = [];
            for(const dep of checkedDepartments) {
                departmentsJSON.push(dep.name);
            }
            this.formData.DepartmentsJSON = JSON.stringify(departmentsJSON);

            const checkedTypes = this.formData.TypesNew.filter((type: any) => {
                return type.isChecked;
            })
            const typesJSON = [];
            for(const type of checkedTypes) {
                typesJSON.push(type.name);
            }
            if(typesJSON.length) {
                this.formData.TypesJSON = JSON.stringify(typesJSON);
            }

            for(const field of this.requiredFields) {
                if(!(this.formData as any)[field]) {
                    alert(field + ' is required');
                    return;
                }
            }

            this.formData.Name = this.formData.Name.trim();
            this.formData.ProcessOwnersJSON = JSON.stringify(this.ownerIDs);
            this.formData.metaUpdate = true;
            (this.formData as any).minorChange = this.unpublishedSave || (this.showSignChoice && this.minorChange);
            // Without statusChosen the server leaves the status as it was
            (this.formData as any).statusChosen = this.showStatus;
            if(this.showStatus) {
                (this.formData as any).NeedsApprovalType = this.status || null;
                (this.formData as any).publishApproved = !this.status;
            }

            this.disableButton = true;

            if(this.formData.isNew && !this.cloneNode) {
                if(this.formData.Type == 'PROCESS') {
                    this.formData.ProcessJSON = this.aiProcessJSON || JSON.stringify(this.defaultProcessJSON);
                } else if(this.formData.Type == 'TIMELINE') {
                    this.formData.TimelineJSON = this.aiTimelineJSON || JSON.stringify(this.defaultTimelineJSON);
                } else if(this.formData.Type == 'SHEET') {
                    // New role sheets start in the seats model (the chosen template's sections, hints included, + one empty
                    // seat); AI drafts are converted when opened
                    this.formData.SheetJSON = this.aiSheetJSON || JSON.stringify(newRoleSheet(this.chosenSheetSections));
                    if(this.aiSheetColsJSON) {
                        this.formData.SheetColsJSON = this.aiSheetColsJSON;
                    }
                } else if(this.formData.Type == 'CHECKLIST') {
                    this.formData.ChecklistJSON = this.aiChecklistJSON || JSON.stringify(this.defaultChecklistJSON);
                }
            }

            // Lets the server send the AI-created confirmation email (PROCESS only, server-gated)
            (this.formData as any).aiGenerated = !!(this.aiProcessJSON || this.aiChecklistJSON || this.aiSheetJSON || this.aiTimelineJSON);

            await axios.post(`${this.$store.state.apiUrl}/api/playbook/node`, this.formData)
                .then(resp => {
                    this.$store.dispatch('setStoreData');
                    if(this.aiVideoJobId && this.formData.Type == 'PROCESS') {
                        // Mark the background video job consumed so its email link can't double-create
                        axios.post(`${this.$store.state.apiUrl}/api/ai/video-jobs/${this.aiVideoJobId}/consume`, { slug: this.formData.Slug })
                            .catch(() => { /* non-fatal */ });
                    }
                    if(this.formData.isNew) {
                        if(this.formData.Type == 'PROCESS') {
                            this.$router.push('/edit-process/' + this.formData.Slug)
                        } else if(this.formData.Type == 'TIMELINE') {
                            this.$router.push('/edit-timeline/' + this.formData.Slug)
                        } else if(this.formData.Type == 'SHEET') {
                            this.$router.push('/edit-sheet/' + this.formData.Slug)
                        } else if(this.formData.Type == 'CHECKLIST') {
                            this.$router.push('/edit-checklist/' + this.formData.Slug)
                        }
                    }
                    this.$emit('close');
                })
                .catch(e => {
                    console.log(e);
                    alert(e?.response?.data || 'There was an error')
                })

            this.disableButton = false;
        },
        setSlug() {
            if(this.formData.isNew) {
                this.formData.Slug = this.formData.Name.toLowerCase().replace(/[^a-zA-Z0-9- ]/g, '').replace(/ /g, '-');
            }
        },
        editSlug() {
            this.formData.Slug = this.formData.Slug.toLowerCase().replace(/[^a-zA-Z0-9- ]/g, '').replace(/ /g, '-');
        },
        onAIDraft(draft: any, jobId?: any) {
            this.aiVideoJobId = jobId || null;
            if(!this.formData.Name) {
                this.formData.Name = draft.name;
                this.setSlug();
            }
            if(this.formData.Type == 'CHECKLIST') {
                // Items arrive already in ChecklistJSON shape (ids assigned server-side)
                this.aiChecklistJSON = JSON.stringify({ items: draft.items });
            } else if(this.formData.Type == 'SHEET') {
                // The seats model, with section and seat ids assigned server-side
                this.aiSheetJSON = JSON.stringify(draft.sheet);
                this.aiSheetColsJSON = JSON.stringify(draft.sheet.seats.map((seat: any) => ({ id: seat.id, accountabilities: seat.accountabilities })));
            } else if(this.formData.Type == 'TIMELINE') {
                // Steps arrive with palette colors assigned server-side
                this.aiTimelineJSON = JSON.stringify({ items: draft.items });
            } else {
                this.aiProcessJSON = JSON.stringify({
                    items: draft.items.map((item: any) => ({
                        header: item.header,
                        body: item.body,
                        image: item.image || '' // step screenshots from video/screen-recording generation
                    }))
                });
            }
            // Pre-check the AI's suggested departments/types where they match company lists
            for(const dep of this.formData.DepartmentsNew) {
                if(draft.suggestedDepartments.includes(dep.name)) {
                    dep.isChecked = true;
                }
            }
            for(const type of this.formData.TypesNew) {
                if(draft.suggestedTypes.includes(type.name)) {
                    type.isChecked = true;
                }
            }
            // Default to all locations if none picked yet (form requires at least one)
            if(!this.formData.LocationsNew.some((loc: any) => loc.isChecked)) {
                for(const loc of this.formData.LocationsNew) {
                    loc.isChecked = true;
                }
            }
            this.showAIModal = false;
        },
        checkLink() {
            if(this.formData.Type == 'VIDEO') {
                this.formData.Link = this.convertVideoEmbedLink(this.formData.Link);
            }
        },
        async onFileChange(file: any) {
            if(file) {
                if(this.formData.Type == 'VIDEO') {
                    const fileNameLower = file.name.toLowerCase();
                    if(!fileNameLower.includes('.mp4') 
                        && !fileNameLower.includes('.webm') 
                        && !fileNameLower.includes('.ogg')) {
                            alert('File is not an embeddable video');
                            return;
                    }
                }

                const formData = new FormData();
                formData.append('file', file);
                await axios.post(`${this.$store.state.apiUrl}/api/playbook/processimage`, formData)
                    .then(resp => {
                        this.formData.Link = this.$store.state.spacesUrl + resp.data.key;
                    })
                    .catch(e => {
                        console.log(e);
                        alert("There was an error.");
                    })
            }
        },
    }
});
</script>
